/**
 * Reviews context (supporting), boundary validation for a review submission
 * and the rating display helper. Pure: no I/O.
 *
 * SHAPE + value ranges only (04-architecture.md §6): whether the booking is
 * unlocked (H13) is decided by Booking (getReviewableBooking, H18); one-per-
 * booking is enforced by the DB unique index. A rating alone is a valid review;
 * the written body is optional (H18 ruling).
 */

import { failure, type Failure, type FailureCode } from "@/modules/shared/failure";

export const RATING_MIN = 1;
export const RATING_MAX = 5;
export const BODY_MAX = 2000;

export interface ReviewSubmission {
  bookingId: string;
  rating: number;
  /** Optional (H18): null when the consumer left only a rating. */
  body: string | null;
}

/**
 * A parse refusal is a coded `Failure`, not a sentence (bM1): the same parser
 * serves the web form and the mobile API, and each renders its own copy from
 * the code. See docs/mobile/api-build-plan.md.
 */
export type ParseResult<T> =
  | { ok: true; data: T }
  | { ok: false; failure: Failure };

/** The codes this parser can refuse with. */
export type ReviewParseCode = Extract<
  FailureCode,
  "malformed_input" | "rating_required" | "review_body_too_long"
>;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The **web's** copy for each refusal code, and the single source of truth for
 * the sentences the screen doc quotes. Other clients render their own words
 * from the same codes.
 */
export const REVIEW_COPY: Record<ReviewParseCode, string> = {
  malformed_input: "That review was malformed. Please try again.",
  rating_required: "Please choose a rating from 1 to 5 stars.",
  review_body_too_long: `Please keep your review under ${BODY_MAX} characters.`,
};

export function parseReviewSubmission(raw: {
  bookingId: unknown;
  rating: unknown;
  body: unknown;
}): ParseResult<ReviewSubmission> {
  const bookingId = String(raw.bookingId ?? "");
  if (!UUID.test(bookingId)) return { ok: false, failure: failure("malformed_input") };

  const rating = Number(raw.rating);
  if (!Number.isInteger(rating) || rating < RATING_MIN || rating > RATING_MAX) {
    return { ok: false, failure: failure("rating_required", { field: "rating" }) };
  }

  const trimmed = String(raw.body ?? "").trim();
  if (trimmed.length > BODY_MAX) {
    return { ok: false, failure: failure("review_body_too_long", { field: "body" }) };
  }
  const body = trimmed.length === 0 ? null : trimmed;

  return { ok: true, data: { bookingId, rating, body } };
}

/**
 * The one-decimal display of a rating roll-up (H5). The view returns a raw
 * AVG; the read model never rounds so the number here is the single place the
 * display precision is decided.
 */
export function formatRating(average: number): string {
  return average.toFixed(1);
}
